import type { TripPanelType } from '@elara/client-core/states/TripsState';

import type { IconName } from '#ui/MaterialIcon.js';

import { cn } from '@elara/client-core/lib/utils';

import { useTripsState } from '#states/hooks.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { SupportButton } from './SupportButton.js';

interface MobileNavTab {
  value: TripPanelType;
  label: string;
  icon?: IconName;
}

export function MobileNavBar({
  tabs,
  animatingValue,
  animatingClassName,
}: {
  tabs: MobileNavTab[];
  animatingValue?: string | null;
  animatingClassName?: (value: string) => string;
}) {
  const tripsState = useTripsState();
  const isStandalone = Boolean(window.navigator.standalone);

  return (
    <nav className={cn('px-4 shrink-0 flex items-center gap-1', isStandalone && 'pb-4')} aria-label="Trip navigation">
      {tabs.map((tab) => {
        const isActive = tripsState.activePanel === tab.value;

        return (
          <button
            key={tab.value}
            type="button"
            data-tour-highlight={`nav-${tab.value}`}
            onClick={() => tripsState.switchToPanel(tab.value)}
            className={cn(
              'flex-1 flex items-center justify-center cursor-pointer rounded-sm',
              'transition-text duration-200 font-medium px-3 pb-3 pt-0',
              isActive ? 'text-primary' : 'text-foreground',
            )}
          >
            <div className="flex items-center gap-1 flex-col">
              {tab.icon && (
                <MaterialIcon
                  icon={tab.icon}
                  size={IconSizes.md}
                  weight="light"
                  filled={isActive || animatingValue === tab.value}
                  className={cn(
                    animatingValue === tab.value && (animatingClassName?.(tab.value) ?? 'animate-nav-pulse'),
                  )}
                />
              )}
              <span className={cn('text-[10px] tracking-wider', isActive && 'font-semibold')}>{tab.label}</span>
            </div>
          </button>
        );
      })}
      <SupportButton variant="ghost" size="icon" isMobile />
    </nav>
  );
}
